import React from 'react';
import Footer from './Footer';

const TermsOfService = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';

  const styles = {
    container: {
      minHeight: '100vh',
      background: isDark ? '#0f0f1e' : '#f9fafb',
      color: isDark ? '#e0e0e0' : '#1f2937',
      paddingTop: '80px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '40px 20px',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '10px',
      color: isDark ? '#ffffff' : '#1f2937',
    },
    lastUpdated: {
      fontSize: '0.9rem',
      color: isDark ? '#9ca3af' : '#6b7280',
      marginBottom: '40px',
    },
    section: {
      marginBottom: '40px',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '15px',
      color: isDark ? '#ffffff' : '#1f2937',
    },
    paragraph: {
      fontSize: '1rem',
      lineHeight: '1.8',
      marginBottom: '15px',
      color: isDark ? '#d0d0d0' : '#4b5563',
    },
    list: {
      marginLeft: '20px',
      marginBottom: '15px',
    },
    listItem: {
      fontSize: '1rem',
      lineHeight: '1.8',
      marginBottom: '10px',
      color: isDark ? '#d0d0d0' : '#4b5563',
    },
    link: {
      color: '#667eea',
      textDecoration: 'none',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.title}>Terms of Service</h1>
        <p style={styles.lastUpdated}>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Acceptance of Terms</h2>
          <p style={styles.paragraph}>
            By accessing or using Recapify's Chrome extension and web application ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use our Service.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Description of Service</h2>
          <p style={styles.paragraph}>
            Recapify provides a browser-based recording tool that enables you to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Record audio and video from browser tabs</li>
            <li style={styles.listItem}>Automatically transcribe recordings</li>
            <li style={styles.listItem}>Generate AI-powered summaries</li>
            <li style={styles.listItem}>Search across your recordings using semantic search</li>
            <li style={styles.listItem}>Store and manage your recordings in the cloud</li>
          </ul>
          <p style={styles.paragraph}>
            <strong>Important Notice:</strong> Recapify is a technology tool that provides recording capabilities. We do not monitor, review, or control the content you record. You are solely responsible for your use of the Service and compliance with all applicable laws.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. Account Registration</h2>
          <p style={styles.paragraph}>
            To use Recapify, you must:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Be at least 18 years of age</li>
            <li style={styles.listItem}>Provide accurate and complete registration information</li>
            <li style={styles.listItem}>Maintain the security of your account credentials</li>
            <li style={styles.listItem}>Provide valid payment information</li>
          </ul>
          <p style={styles.paragraph}>
            You are responsible for all activities that occur under your account.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Pricing and Payment</h2>
          <p style={styles.paragraph}>
            Recapify operates on a pay-as-you-go model:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>No subscription fees or fixed monthly charges</li>
            <li style={styles.listItem}>You pay only for resources consumed (storage, transcription, AI processing, search)</li>
            <li style={styles.listItem}>Invoices are sent monthly for the previous month's usage</li>
            <li style={styles.listItem}>Payment is processed through Stripe</li>
            <li style={styles.listItem}>Prices are subject to change with 30 days notice</li>
            <li style={styles.listItem}>You can choose transcription methods to balance speed and cost</li>
          </ul>
          <p style={styles.paragraph}>
            You can view your current usage and estimated costs in the web application at any time.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>5. User Responsibilities and Legal Compliance</h2>
          
          <p style={styles.paragraph}><strong>5.1 Recording Consent and Legal Obligations</strong></p>
          <p style={styles.paragraph}>
            You acknowledge and agree that you are solely responsible for:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Obtaining all necessary consents, permissions, and authorizations before recording any person, conversation, meeting, or content</li>
            <li style={styles.listItem}>Complying with all applicable federal, state, local, and international laws regarding recording, including but not limited to wiretapping laws, privacy laws, and consent requirements</li>
            <li style={styles.listItem}>Understanding and complying with the recording consent laws in your jurisdiction and the jurisdiction of all recorded parties</li>
            <li style={styles.listItem}>Providing appropriate notice to all parties that a recording is taking place when required by law</li>
            <li style={styles.listItem}>Determining whether one-party or all-party consent is required in your jurisdiction</li>
          </ul>
          
          <p style={styles.paragraph}><strong>5.2 Prohibited Uses</strong></p>
          <p style={styles.paragraph}>You expressly agree NOT to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Record any person without their knowledge or consent where such consent is required by law</li>
            <li style={styles.listItem}>Record content that you do not have the legal right to record</li>
            <li style={styles.listItem}>Use the Service to violate any person's privacy rights, publicity rights, or other legal rights</li>
            <li style={styles.listItem}>Record content that infringes on intellectual property rights, including copyrighted material, trade secrets, or confidential information</li>
            <li style={styles.listItem}>Use the Service for any unlawful, fraudulent, or malicious purpose</li>
            <li style={styles.listItem}>Record content containing illegal material or activity</li>
            <li style={styles.listItem}>Attempt to circumvent security measures, access restrictions, or usage limitations</li>
            <li style={styles.listItem}>Use the Service in any manner that could harm, disable, overburden, or impair our systems</li>
            <li style={styles.listItem}>Interfere with any other user's use and enjoyment of the Service</li>
          </ul>
          
          <p style={styles.paragraph}><strong>5.3 Disclaimer of Responsibility</strong></p>
          <p style={styles.paragraph}>
            <strong>RECAPIFY PROVIDES ONLY THE TECHNOLOGY TOOL FOR RECORDING.</strong> We do not and cannot monitor, control, or verify the legality of your recordings. We are not responsible for:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Your failure to obtain required consents or permissions</li>
            <li style={styles.listItem}>Your violation of any recording, privacy, or wiretapping laws</li>
            <li style={styles.listItem}>Any legal consequences arising from your use of the Service</li>
            <li style={styles.listItem}>The content, legality, or accuracy of any recordings you create</li>
            <li style={styles.listItem}>Any claims, damages, or liabilities arising from your recordings</li>
          </ul>
          
          <p style={styles.paragraph}><strong>5.4 Jurisdictional Variations</strong></p>
          <p style={styles.paragraph}>
            Recording laws vary significantly by jurisdiction. Some jurisdictions require all-party consent (all participants must agree to be recorded), while others require only one-party consent (only one participant needs to consent). It is YOUR responsibility to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Research and understand the recording laws applicable to your situation</li>
            <li style={styles.listItem}>Consult with legal counsel if you are uncertain about recording requirements</li>
            <li style={styles.listItem}>Comply with the most restrictive law when recording parties in multiple jurisdictions</li>
          </ul>
          
          <p style={styles.paragraph}><strong>5.5 General Conduct</strong></p>
          <p style={styles.paragraph}>You further agree to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Use the Service only for lawful purposes</li>
            <li style={styles.listItem}>Maintain the security and confidentiality of your account credentials</li>
            <li style={styles.listItem}>Promptly notify us of any unauthorized use of your account</li>
            <li style={styles.listItem}>Accept full responsibility for all activities conducted through your account</li>
            <li style={styles.listItem}>Maintain your own backups of important recordings</li>
          </ul>
          
          <p style={styles.paragraph}>
            <strong>WARNING:</strong> Unauthorized recording may result in civil liability, criminal prosecution, or both. Penalties can include substantial fines and imprisonment. By using this Service, you acknowledge that you understand these risks and accept full responsibility for your compliance with all applicable laws.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Content Ownership and License</h2>
          <p style={styles.paragraph}>
            You retain all ownership rights to your recordings and content. By using our Service, you grant us a limited license to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Store your recordings on our servers</li>
            <li style={styles.listItem}>Process recordings for transcription and AI summaries</li>
            <li style={styles.listItem}>Display your content back to you through the application</li>
          </ul>
          <p style={styles.paragraph}>
            We will never use your content for any other purpose, including training AI models or sharing with third parties.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Service Availability</h2>
          <p style={styles.paragraph}>
            We strive to provide reliable service but do not guarantee:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Uninterrupted or error-free operation</li>
            <li style={styles.listItem}>100% accuracy of transcriptions or AI summaries</li>
            <li style={styles.listItem}>Compatibility with all websites or platforms</li>
            <li style={styles.listItem}>Availability during maintenance periods</li>
          </ul>
          <p style={styles.paragraph}>
            We reserve the right to modify or discontinue the Service with reasonable notice.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Data Backup and Loss</h2>
          <p style={styles.paragraph}>
            While we implement robust backup systems, you are responsible for maintaining your own backups of important recordings. We are not liable for any data loss, corruption, or deletion.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Account Termination</h2>
          <p style={styles.paragraph}>
            You may close your account at any time through the web application. Upon account closure:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>All recordings and data will be permanently deleted within 24 hours</li>
            <li style={styles.listItem}>A final invoice will be sent for any outstanding charges</li>
            <li style={styles.listItem}>Access to the Service will be immediately revoked</li>
            <li style={styles.listItem}>This action cannot be undone</li>
          </ul>
          <p style={styles.paragraph}>
            We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Limitation of Liability and Disclaimers</h2>
          
          <p style={styles.paragraph}><strong>10.1 Service Provided "AS IS"</strong></p>
          <p style={styles.paragraph}>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
          </p>
          
          <p style={styles.paragraph}><strong>10.2 No Warranty</strong></p>
          <p style={styles.paragraph}>
            Recapify makes no warranty that:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>The Service will meet your requirements or expectations</li>
            <li style={styles.listItem}>The Service will be uninterrupted, timely, secure, or error-free</li>
            <li style={styles.listItem}>The results obtained from using the Service will be accurate or reliable</li>
            <li style={styles.listItem}>Transcriptions or AI summaries will be 100% accurate</li>
            <li style={styles.listItem}>The Service will be compatible with all websites, platforms, or devices</li>
            <li style={styles.listItem}>Any errors or defects will be corrected</li>
          </ul>
          
          <p style={styles.paragraph}><strong>10.3 Limitation of Liability</strong></p>
          <p style={styles.paragraph}>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL RECAPIFY, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Any indirect, incidental, special, consequential, or punitive damages</li>
            <li style={styles.listItem}>Loss of profits, revenue, data, use, goodwill, or other intangible losses</li>
            <li style={styles.listItem}>Damages resulting from your use or inability to use the Service</li>
            <li style={styles.listItem}>Unauthorized access to or alteration of your recordings or data</li>
            <li style={styles.listItem}>Any conduct or content of any third party on or through the Service</li>
            <li style={styles.listItem}>Legal consequences arising from your recordings or failure to comply with recording laws</li>
            <li style={styles.listItem}>Claims by third parties related to your recordings</li>
          </ul>
          
          <p style={styles.paragraph}>
            OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO RECAPIFY IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
          </p>
          
          <p style={styles.paragraph}><strong>10.4 Third-Party Services</strong></p>
          <p style={styles.paragraph}>
            We are not responsible or liable for the actions, products, services, or content of third-party service providers. Your use of third-party services is at your own risk.
          </p>
          
          <p style={styles.paragraph}><strong>10.5 Jurisdictional Limitations</strong></p>
          <p style={styles.paragraph}>
            Some jurisdictions do not allow the exclusion of certain warranties or the limitation of liability for incidental or consequential damages. In such jurisdictions, our liability will be limited to the maximum extent permitted by law.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Indemnification</h2>
          <p style={styles.paragraph}>
            You agree to defend, indemnify, and hold harmless Recapify, its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, debts, and expenses (including but not limited to attorney's fees) arising from:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Your use or misuse of the Service</li>
            <li style={styles.listItem}>Your violation of these Terms</li>
            <li style={styles.listItem}>Your violation of any laws, regulations, or third-party rights, including but not limited to recording laws, privacy laws, wiretapping laws, intellectual property rights, or publicity rights</li>
            <li style={styles.listItem}>Any recordings you create, including their content, creation, storage, or distribution</li>
            <li style={styles.listItem}>Your failure to obtain required consents or permissions for recording</li>
            <li style={styles.listItem}>Any claims by third parties related to your recordings or use of the Service</li>
            <li style={styles.listItem}>Your breach of any representation or warranty contained in these Terms</li>
          </ul>
          <p style={styles.paragraph}>
            This indemnification obligation will survive termination of your account and these Terms.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>12. Intellectual Property</h2>
          <p style={styles.paragraph}>
            The Recapify Service, including its design, code, and branding, is protected by intellectual property laws. You may not copy, modify, or reverse engineer any part of our Service.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>13. Changes to Terms</h2>
          <p style={styles.paragraph}>
            We may update these Terms from time to time. Significant changes will be communicated via email or through the application. Continued use after changes constitutes acceptance of the updated Terms.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>14. Governing Law</h2>
          <p style={styles.paragraph}>
            These Terms are governed by and construed in accordance with applicable laws. Any disputes shall be resolved through binding arbitration.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>15. Contact Information</h2>
          <p style={styles.paragraph}>
            For questions about these Terms, please contact us at:
          </p>
          <p style={styles.paragraph}>
            Email: <a href="mailto:support@recapifyapp.com" style={styles.link}>support@recapifyapp.com</a>
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>16. Severability</h2>
          <p style={styles.paragraph}>
            If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
          </p>
        </div>
      </div>
      <Footer theme={theme} />
    </div>
  );
};

export default TermsOfService;
